import { gql } from "@apollo/client";

// ============================================================================
// Slack Connection Status Types
// ============================================================================

export interface SlackConnectionStatus {
  connected: boolean;
  teamName?: string;
  channelName?: string;
  connectedAt?: string;
  lastActivity?: string;
}

export interface SlackOAuthResult {
  success: boolean;
  authUrl?: string;
  error?: string;
}

export interface SlackDisconnectResult {
  success: boolean;
  error?: string;
}

// ============================================================================
// GraphQL Queries
// ============================================================================

export const GET_SLACK_CONNECTION_STATUS = gql`
  query GetSlackConnectionStatus {
    getSlackConnectionStatus {
      connected
      teamName
      channelName
      connectedAt
      lastActivity
    }
  }
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

export const INITIATE_SLACK_OAUTH = gql`
  mutation InitiateSlackOAuth($redirectUrl: String) {
    initiateSlackOAuth(redirectUrl: $redirectUrl) {
      success
      authUrl
      error
    }
  }
`;

export const DISCONNECT_SLACK = gql`
  mutation DisconnectSlack {
    disconnectSlack {
      success
      error
    }
  }
`;

export const CONFIGURE_SLACK = gql`
  mutation ConfigureSlack($input: SlackConfigurationInput!) {
    configureSlack(input: $input) {
      success
      configuration {
        tenantId
        configId
        webhookUrl
        channelId
        channelName
        teamId
        teamName
        isActive
        notificationSettings {
          notifyOnNewConversation
          notifyOnCustomerMessage
          notifyOnAIResponse
          mentionUsers
          quietHours {
            start
            end
            timezone
          }
        }
        lastTestResult {
          success
          timestamp
          error
          responseTime
        }
        createdAt
        updatedAt
        createdBy
      }
      error
    }
  }
`;

export const TEST_SLACK_CONNECTION = gql`
  mutation TestSlackConnection {
    testSlackConnection {
      success
      responseTime
      error
      timestamp
    }
  }
`;

export const GET_SLACK_CHANNELS = gql`
  query GetSlackChannels {
    getSlackChannels {
      success
      channels {
        id
        name
        isPrivate
        isMember
      }
      error
    }
  }
`;

export const UPDATE_SLACK_CHANNEL = gql`
  mutation UpdateSlackChannel($channelId: String!, $channelName: String!) {
    updateSlackChannel(channelId: $channelId, channelName: $channelName) {
      success
      configuration {
        tenantId
        configId
        webhookUrl
        channelId
        channelName
        teamId
        teamName
        isActive
        notificationSettings {
          notifyOnNewConversation
          notifyOnCustomerMessage
          notifyOnAIResponse
          mentionUsers
          quietHours {
            start
            end
            timezone
          }
        }
        lastTestResult {
          success
          timestamp
          error
          responseTime
        }
        createdAt
        updatedAt
        createdBy
      }
      error
    }
  }
`;

export const GET_SLACK_CONFIGURATION = gql`
  query GetSlackConfiguration {
    getSlackConfiguration {
      tenantId
      configId
      webhookUrl
      channelId
      channelName
      teamId
      teamName
      isActive
      notificationSettings {
        notifyOnNewConversation
        notifyOnCustomerMessage
        notifyOnAIResponse
        mentionUsers
        quietHours {
          start
          end
          timezone
        }
      }
      lastTestResult {
        success
        timestamp
        error
        responseTime
      }
      createdAt
      updatedAt
      createdBy
    }
  }
`;

// ============================================================================
// Input Types
// ============================================================================

export interface SlackConfigurationInput {
  webhookUrl: string;
  channelId: string;
  channelName: string;
  botToken: string;
  signingSecret: string;
  teamId: string;
  teamName: string;
  notificationSettings: SlackNotificationSettingsInput;
}

export interface SlackNotificationSettingsInput {
  notifyOnNewConversation: boolean;
  notifyOnCustomerMessage: boolean;
  notifyOnAIResponse: boolean;
  mentionUsers: string[];
  quietHours?: QuietHoursInput;
}

export interface QuietHoursInput {
  start: string;
  end: string;
  timezone: string;
}

// ============================================================================
// Response Types
// ============================================================================

export interface SlackConfiguration {
  tenantId: string;
  configId: string;
  webhookUrl: string;
  channelId: string;
  channelName: string;
  teamId: string;
  teamName: string;
  isActive: boolean;
  notificationSettings: SlackNotificationSettings;
  lastTestResult?: SlackTestResult;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface SlackNotificationSettings {
  notifyOnNewConversation: boolean;
  notifyOnCustomerMessage: boolean;
  notifyOnAIResponse: boolean;
  mentionUsers: string[];
  quietHours?: QuietHours;
}

export interface QuietHours {
  start: string;
  end: string;
  timezone: string;
}

export interface SlackTestResult {
  success: boolean;
  responseTime?: number;
  error?: string;
  timestamp: string;
}

export interface SlackConfigurationResult {
  success: boolean;
  configuration?: SlackConfiguration;
  error?: string;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Open Slack OAuth popup and handle the flow
 */
export function openSlackOAuthPopup(authUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    console.log("Opening Slack OAuth popup:", authUrl);

    const popup = window.open(
      authUrl,
      "slack-oauth",
      "width=600,height=700,scrollbars=yes,resizable=yes,location=yes"
    );

    if (!popup) {
      console.error("Failed to open popup - popup blocker may be active");
      resolve(false);
      return;
    }

    let resolved = false;

    // Listen for messages from the popup (for OAuth callback)
    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === "SLACK_OAUTH_SUCCESS") {
        console.log("Received OAuth success message");
        cleanup();
        resolved = true;
        resolve(true);
      } else if (event.data.type === "SLACK_OAUTH_ERROR") {
        console.error("Received OAuth error message:", event.data.error);
        cleanup();
        resolved = true;
        resolve(false);
      }
    };

    window.addEventListener("message", messageListener);

    // Poll for popup closure as fallback
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        console.log("Popup closed by user");
        cleanup();
        if (!resolved) {
          resolved = true;
          resolve(true); // Assume success if popup was closed normally
        }
      }
    }, 1000);

    // Cleanup function
    const cleanup = () => {
      clearInterval(pollTimer);
      window.removeEventListener("message", messageListener);
      if (!popup.closed) {
        popup.close();
      }
    };

    // Timeout after 5 minutes
    setTimeout(() => {
      if (!resolved) {
        console.log("OAuth popup timed out");
        cleanup();
        resolved = true;
        resolve(false);
      }
    }, 300000);
  });
}

/**
 * Format date for display
 */
export function formatSlackDate(dateString?: string): string {
  if (!dateString) return "Never";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Validate Slack webhook URL format
 */
export function isValidSlackWebhookUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname === "hooks.slack.com" &&
      urlObj.pathname.startsWith("/services/")
    );
  } catch {
    return false;
  }
}

/**
 * Validate Slack channel ID format
 */
export function isValidSlackChannelId(channelId: string): boolean {
  return /^[C][A-Z0-9]{8,}$/.test(channelId);
}
